import inquirer from 'inquirer';
import { execa } from 'execa';
import { getLastUpdateCheckAt, setLastUpdateCheck } from './active-org.js';

interface NpmDist {
  version: string;
}

/**
 * Default shell-out: runs `npm install -g @cloudgrid-io/cli@latest` and
 * inherits stdio so the user sees npm's progress. Test seam — overridable
 * via {@link CheckForUpdatesOpts.shellOut} so unit tests can assert the
 * confirm-prompt path without actually shelling out to npm.
 */
async function defaultShellOut(): Promise<{ ok: boolean; error?: string }> {
  try {
    await execa('npm', ['install', '-g', '@cloudgrid-io/cli@latest'], { stdio: 'inherit' });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export interface CheckForUpdatesOpts {
  /** Override the TTY check (defaults to `process.stdin.isTTY`). */
  isTty?: boolean;
  /** Override `process.env.CLOUDGRID_NO_PROMPT` lookup. */
  noPrompt?: boolean;
  /** Override the registry URL (test seam). */
  registryUrl?: string;
  /**
   * Confirm prompt — defaults to inquirer's confirm. Returns true if the
   * user confirms the update.
   */
  confirm?: (latest: string, current: string) => Promise<boolean>;
  /** Shell-out runner. Override in tests so we don't run npm. */
  shellOut?: () => Promise<{ ok: boolean; error?: string }>;
  /** When set, skip the per-day throttle so tests can drive the path. */
  ignoreThrottle?: boolean;
}

/**
 * Backlog 200 — Level 2 self-update prompt.
 *
 * On startup, if a newer `latest` exists on npm AND we're attached to a
 * TTY AND `CLOUDGRID_NO_PROMPT` is not set, prompt the user to upgrade.
 * On confirm, shell out to `npm install -g @cloudgrid-io/cli@latest` so
 * the next invocation runs on the new bits.
 *
 * Non-interactive contexts (CI, piped stdin, `CLOUDGRID_NO_PROMPT=1`)
 * fall back to the legacy passive notice — they're scripted environments
 * where surprising the operator with a blocking prompt would be worse
 * than the drift.
 *
 * Level 1 (the API's `426 UPGRADE_REQUIRED` floor) is the actual gate.
 * This prompt is the friendly nudge that prevents users from ever hitting
 * it in the first place.
 */
export async function checkForUpdates(
  currentVersion: string,
  opts: CheckForUpdatesOpts = {},
): Promise<void> {
  if (!opts.ignoreThrottle) {
    const last = getLastUpdateCheckAt();
    if (last && Date.now() - new Date(last).getTime() < 24 * 3600 * 1000) return;
  }

  const registryUrl = opts.registryUrl ?? 'https://registry.npmjs.org/@cloudgrid-io/cli/latest';

  let latest: string;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    const res = await fetch(registryUrl, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return;
    const body = (await res.json()) as NpmDist;
    if (!body?.version) return;
    latest = body.version;
  } catch {
    return;
  }

  setLastUpdateCheck(new Date().toISOString(), latest);
  if (latest === currentVersion || !isNewer(latest, currentVersion)) return;

  const noPrompt = opts.noPrompt ?? Boolean(process.env.CLOUDGRID_NO_PROMPT);
  const isTty = opts.isTty ?? Boolean(process.stdin.isTTY && process.stdout.isTTY);

  if (noPrompt || !isTty) {
    process.stderr.write(
      `[A new version of CloudGrid CLI is available: ${latest} (you have ${currentVersion})]\n`,
    );
    process.stderr.write(`[Update with: npm install -g @cloudgrid-io/cli@latest]\n`);
    return;
  }

  const confirm = opts.confirm ?? defaultConfirm;
  let answer: boolean;
  try {
    answer = await confirm(latest, currentVersion);
  } catch {
    return;
  }
  if (!answer) return;

  const shellOut = opts.shellOut ?? defaultShellOut;
  process.stderr.write(`Updating to @cloudgrid-io/cli@${latest}…\n`);
  const result = await shellOut();
  if (result.ok) {
    process.stderr.write(`Updated. Re-run your command to use ${latest}.\n`);
  } else {
    process.stderr.write(`Update failed: ${result.error ?? 'unknown error'}\n`);
    process.stderr.write(`Run manually: npm install -g @cloudgrid-io/cli@latest\n`);
  }
}

async function defaultConfirm(latest: string, current: string): Promise<boolean> {
  const ans = await inquirer.prompt<{ ok: boolean }>([
    {
      type: 'confirm',
      name: 'ok',
      message: `A new CloudGrid CLI is available: ${latest} (you have ${current}). Update now?`,
      default: true,
    },
  ]);
  return ans.ok;
}

function isNewer(a: string, b: string): boolean {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return true;
    if (pa[i] < pb[i]) return false;
  }
  return false;
}
